import { STORAGE } from '@webready/shared/config';
import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '@/lib/api';
import { getSchemaScopedQueryKey } from '@/lib/schemaStorage';
import { applyImageSurfaceTheme, getPrimaryDarkColor, isDarkBackgroundColor } from '@/lib/utils';
import { readDesignCustomCache } from '@/lib/designCustomCache';
import staticAppSettings from '@/constants/app-settings.json';

export interface ThemeSettings {
  theme_name: string;
  primary_color: string;
  secondary_color: string;
  background_color: string;
  text_color: string;
  brand_color: string;
  dark_mode_enabled: boolean;
}

interface ThemeContextType {
  theme: ThemeSettings | null;
  isLoading: boolean;
  applyTheme: (theme: ThemeSettings) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}

interface ThemeProviderProps {
  children: ReactNode;
}

export function ThemeProvider({ children }: ThemeProviderProps) {
  const initialThemeSettings: ThemeSettings = {
    theme_name: staticAppSettings.theme_name || 'Muted Violet',
    primary_color: staticAppSettings.primary_color || '#7A6EA1',
    secondary_color: staticAppSettings.secondary_color || '#B6A9D1',
    background_color: staticAppSettings.background_color || '#F7F6FA',
    text_color: staticAppSettings.text_color || '#24222D',
    brand_color: staticAppSettings.brand_color || staticAppSettings.primary_color || '#7A6EA1',
    dark_mode_enabled: false
  };

  const [theme, setTheme] = useState<ThemeSettings>(initialThemeSettings);
  const [currentObserver, setCurrentObserver] = useState<MutationObserver | null>(null);

  const { data: settings, isLoading } = useQuery({
    queryKey: getSchemaScopedQueryKey(['app-settings']),
    queryFn: async () => {
      const response = await api.get('app-settings');
      return response.data;
    },
  });

  // Helper function to check if an element should be excluded from theme color application
  const shouldExcludeFromThemeColor = (element: HTMLElement): boolean => {
    // Navbar + explicit opt-out regions manage their own foreground
    if (element.closest('#site-navbar, [data-no-theme-colors]')) {
      return true;
    }

    // Exclude elements inside lexical editor content or static page content
    const isInLexicalContent = element.closest('.static-page-content, .lexical-tailwind-styles, .lexical-editor-content');
    if (isInLexicalContent) {
      // Only exclude if element has inline color style (preserve lexical editor colors)
      const hasInlineColor = element.style.color && element.style.color !== '';
      if (hasInlineColor) {
        return true;
      }
    }
    
    // Liquid glass panels over video: keep Tailwind text utilities (white/light)
    if (
      document.body.classList.contains('site-video-backdrop') ||
      document.body.classList.contains('home-fixed-video-backdrop')
    ) {
      if (element.closest('.custom-liquid-glass')) {
        return true;
      }
    }

    // Tailwind opacity variants e.g. text-white/80, !text-white/90
    if (
      element.className.includes('text-white') ||
      element.className.includes('text-black')
    ) {
      return true;
    }
    
    // Check if element has inline style with white/black color
    const inlineColor = element.style.color;
    if (inlineColor) {
      const normalizedColor = inlineColor.toLowerCase().trim();
      // Check for white colors
      if (normalizedColor === 'white' || 
          normalizedColor === '#ffffff' || 
          normalizedColor === '#fff' ||
          normalizedColor === 'rgb(255, 255, 255)' ||
          normalizedColor.startsWith('rgba(255, 255, 255')) {
        return true;
      }
      // Check for black colors
      if (normalizedColor === 'black' || 
          normalizedColor === '#000000' ||
          normalizedColor === '#000' ||
          normalizedColor === 'rgb(0, 0, 0)' ||
          normalizedColor.startsWith('rgba(0, 0, 0')) {
        return true;
      }
    }
    
    // Parent with light/dark text utilities (incl. text-white/90)
    const parentWithTextColor = element.closest('[class*="text-white"], [class*="text-black"]');
    if (parentWithTextColor) {
      return true;
    }
    
    // Check if element is inside a button or card with background color
    const parentWithBg = element.closest('button, [role="button"], a[class*="button"], [class*="card"], [class*="Card"]');
    if (parentWithBg instanceof HTMLElement) {
      const bgColor = parentWithBg.style.backgroundColor;
      if (bgColor && bgColor !== 'transparent' && bgColor !== 'rgba(0, 0, 0, 0)') {
        return true;
      }
      // Also check if parent has a background color class
      const hasBgClass = parentWithBg.className.includes('bg-') && 
        !parentWithBg.className.includes('bg-transparent') &&
        !parentWithBg.className.includes('bg-white');
      if (hasBgClass) {
        return true;
      }
    }
    
    // Check if element itself is a button or link with background color
    if (element.tagName === 'BUTTON' || element.tagName === 'A' || element.getAttribute('role') === 'button') {
      const bgColor = element.style.backgroundColor;
      if (bgColor && bgColor !== 'transparent' && bgColor !== 'rgba(0, 0, 0, 0)') {
        return true;
      }
    }
    
    return false;
  };

  // Function to apply theme to dynamically loaded content
  const applyThemeToNewElements = (themeSettings: ThemeSettings) => {
    const observer = new MutationObserver((mutations) => {
      // Check if we're on a dashboard page
      const isDashboardPage = window.location.pathname.includes('/dashboard');
      
      // Don't apply theme to new elements on dashboard pages
      if (isDashboardPage) {
        return;
      }
      
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node as Element;
            
            // Apply text color to new text elements
            const newTextElements = element.querySelectorAll(`
              h1, h2, h3, h4, h5, h6,
              p, span, div, li, td, th,
              .text-gray-900, .text-gray-800, .text-gray-700, .text-gray-600,
              .text-slate-900, .text-slate-800, .text-slate-700, .text-slate-600
            `);
            
            newTextElements.forEach((textElement) => {
              if (textElement instanceof HTMLElement) {
                // Skip elements that should be excluded from theme color application
                if (shouldExcludeFromThemeColor(textElement)) {
                  return;
                }
                
                // Check if parent has primary background
                const parent = textElement.closest('.bg-primary, [class*="bg-primary"], [data-footer-variant="dark-glass"], [class*="from-primary"], [class*="to-primary"]');
                if (parent) {
                  // Force white text on primary background
                  textElement.style.color = '#FFFFFF';
                } else {
                  // Skip elements that already have specific colors
                  const hasSpecificColor = textElement.className.includes('text-') && 
                    !textElement.className.includes('text-gray-') && 
                    !textElement.className.includes('text-slate-');
                  
                  if (!hasSpecificColor) {
                    textElement.style.color = themeSettings.text_color;
                  }
                }
              }
            });
            
            // Apply white text to new primary background elements
            const newPrimaryElements = element.querySelectorAll(`
              .bg-primary, 
              [class*="bg-primary"],
              [data-footer-variant="dark-glass"],
              [class*="from-primary"],
              [class*="to-primary"],
              [class*="bg-gradient-to-r from-primary"],
              [class*="bg-gradient-to-l from-primary"],
              [class*="bg-gradient-to-t from-primary"],
              [class*="bg-gradient-to-b from-primary"],
              [class*="bg-gradient-to-r to-primary"],
              [class*="bg-gradient-to-l to-primary"],
              [class*="bg-gradient-to-t to-primary"],
              [class*="bg-gradient-to-b to-primary"]
            `);
            
            newPrimaryElements.forEach((primaryElement) => {
              if (primaryElement instanceof HTMLElement) {
                primaryElement.style.color = '#FFFFFF';
                
                // Apply white text to all text elements inside
                const textElementsInside = primaryElement.querySelectorAll(`
                  h1, h2, h3, h4, h5, h6,
                  p, span, div, li, td, th,
                  label, input, textarea, select
                `);
                textElementsInside.forEach((textElement) => {
                  if (textElement instanceof HTMLElement) {
                    textElement.style.color = '#FFFFFF';
                  }
                });
              }
            });
            
            // Apply theme to the element itself if it's a text element
            if (element instanceof HTMLElement) {
              // Skip elements that should be excluded from theme color application
              if (shouldExcludeFromThemeColor(element)) {
                return;
              }
              
              const isTextElement = element.tagName.match(/^(H[1-6]|P|SPAN|DIV|LI|TD|TH|LABEL)$/i);
              const hasTextClass = element.className.includes('text-gray-') || element.className.includes('text-slate-');
              const hasPrimaryBg = element.className.includes('bg-primary') || 
                element.className.includes('from-primary') || 
                element.className.includes('to-primary') || 
                element.matches('[data-footer-variant="dark-glass"]');
              
              if (hasPrimaryBg && !element.classList.contains('theme-applied')) {
                // Force white text on primary background elements
                element.style.color = '#FFFFFF';
                element.classList.add('theme-applied');
              } else if ((isTextElement || hasTextClass) && !element.classList.contains('theme-applied')) {
                if (shouldExcludeFromThemeColor(element)) {
                  return;
                }
                const hasSpecificColor = element.className.includes('text-') && 
                  !element.className.includes('text-gray-') && 
                  !element.className.includes('text-slate-');
                
                if (!hasSpecificColor) {
                  element.style.color = themeSettings.text_color;
                }
              }
            }
          }
        });
      });
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
    
    return observer;
  };

  const applyTheme = (themeSettings: ThemeSettings) => {
    setTheme(themeSettings);
    
    // Disconnect previous observer if exists
    if (currentObserver) {
      currentObserver.disconnect();
    }
    
    // Check if we're on a dashboard page
    const isDashboardPage = window.location.pathname.includes('/dashboard');
    
    // Apply theme to CSS custom properties
    const root = document.documentElement;
    root.style.setProperty('--primary-color', themeSettings.primary_color);
    root.style.setProperty('--primary-dark-color', getPrimaryDarkColor(themeSettings.primary_color));
    root.style.setProperty('--secondary-color', themeSettings.secondary_color);
    root.style.setProperty('--background-color', themeSettings.background_color);
    root.style.setProperty('--text-color', themeSettings.text_color);

    // Save to localStorage for instant index.html preloading
    try {
      localStorage.setItem(STORAGE.keys.themePrimaryColor, themeSettings.primary_color);
      localStorage.setItem(STORAGE.keys.themeSecondaryColor, themeSettings.secondary_color);
      localStorage.setItem(STORAGE.keys.themeBackgroundColor, themeSettings.background_color);
      localStorage.setItem(STORAGE.keys.themeTextColor, themeSettings.text_color);
    } catch (e) {
      /* ignore */
    }

    const isHomeFixedVideoBackdrop =
      document.body.classList.contains('site-video-backdrop') ||
      document.body.classList.contains('home-fixed-video-backdrop') ||
      settings?.design_custom === true ||
      readDesignCustomCache();

    const useDarkImageSurface =
      isHomeFixedVideoBackdrop || isDarkBackgroundColor(themeSettings.background_color);
    applyImageSurfaceTheme(root, { dark: useDarkImageSurface });
    
    // On dashboard pages, don't apply background and text colors
    if (isDashboardPage) {
      return;
    }

    // Apply background color to body and html IMMEDIATELY to prevent CLS
    if (isHomeFixedVideoBackdrop) {
      const isLayoutMounted = document.body.classList.contains('site-video-backdrop');
      const fallbackDarkBg = '#0d0618';
      
      document.body.style.setProperty('background-color', isLayoutMounted ? 'transparent' : fallbackDarkBg, 'important');
      document.documentElement.style.setProperty(
        'background-color',
        isLayoutMounted ? 'transparent' : fallbackDarkBg,
        'important'
      );
    } else {
      document.body.style.backgroundColor = themeSettings.background_color;
      document.documentElement.style.backgroundColor = themeSettings.background_color;
    }
    
    // Apply text color to body IMMEDIATELY
    document.body.style.color = themeSettings.text_color;
    
    // Update CSS variables IMMEDIATELY to prevent CLS
    // This is faster than inline styles and prevents layout shifts
    document.documentElement.style.setProperty('--theme-bg-color', themeSettings.background_color);
    document.documentElement.style.setProperty('--theme-text-color', themeSettings.text_color);
    
    // Apply theme to main element via CSS variables (already set in critical CSS)
    // No need for inline styles - CSS variables update instantly without CLS
    
    // Always remove dark mode class since we only have light themes
    document.documentElement.classList.remove('dark');
    
    // Start observing for new elements
    const observer = applyThemeToNewElements(themeSettings);
    setCurrentObserver(observer);
    
    // Apply theme colors with a small delay to ensure DOM is ready
    setTimeout(() => {
      // Check if we're on a dashboard page
      const isDashboardPage = window.location.pathname.includes('/dashboard');
      
      // Don't apply theme colors on dashboard pages
      if (isDashboardPage) {
        return;
      }

      const isHomeFixedVideoBackdrop =
        document.body.classList.contains('site-video-backdrop') ||
        document.body.classList.contains('home-fixed-video-backdrop') ||
        settings?.design_custom === true ||
        readDesignCustomCache();

      // Strip theme colors forced onto navbar (e.g. bg-white/10 ghost buttons)
      document.querySelectorAll('#site-navbar, #site-navbar *').forEach((el) => {
        if (el instanceof HTMLElement) {
          el.style.removeProperty('color');
          el.style.removeProperty('background-color');
          el.classList.remove('theme-applied');
        }
      });

      // Apply theme colors to main content areas
      // Note: main element already has background-color set inline to prevent CLS
      const mainElements = document.querySelectorAll('.bg-slate-100, .bg-background, .main-content, .page-content');
      mainElements.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.style.backgroundColor = themeSettings.background_color;
          element.style.color = themeSettings.text_color;
        }
      });
      
      // Main element background is already set inline in MainLayout to prevent CLS
      // No need to update it here as it's already applied immediately in applyTheme
      
      // Apply theme colors to cards and other UI elements
      const cardElements = document.querySelectorAll(`
        .card, 
        [class*="bg-white"], 
        [class*="bg-slate-50"], 
        [class*="bg-slate-100"],
        [class*="bg-gray-50"], 
        [class*="bg-gray-100"],
        .bg-white,
        .bg-slate-50,
        .bg-slate-100,
        .bg-gray-50,
        .bg-gray-100
      `);
      cardElements.forEach((element) => {
        if (element instanceof HTMLElement) {
          if (element.closest('#site-navbar, [data-no-theme-colors]')) {
            return;
          }
          // Tailwind opacity utilities (bg-white/10) are glass — not solid cards
          if (/\bbg-white\//.test(element.className)) {
            return;
          }
          if (
            element.classList.contains('custom-liquid-glass') ||
            element.closest('.custom-liquid-glass')
          ) {
            return;
          }
          if (
            isHomeFixedVideoBackdrop &&
            (element.classList.contains('bg-transparent') ||
              element.closest('[role="banner"]') ||
              element.closest('.hero-section-wrapper') ||
              element.closest('main'))
          ) {
            return;
          }
          element.style.backgroundColor = themeSettings.background_color;
          element.style.color = themeSettings.text_color;
          element.classList.add('theme-applied');
        }
      });
      
      // Apply text color to all text elements
      // Exclude elements inside lexical editor content to preserve inline styles
      const textElements = document.querySelectorAll(`
        h1, h2, h3, h4, h5, h6,
        p, span, div, li, td, th,
        .text-gray-900, .text-gray-800, .text-gray-700, .text-gray-600,
        .text-slate-900, .text-slate-800, .text-slate-700, .text-slate-600,
        .text-black, .text-white,
        label, input, textarea, select
      `);
      textElements.forEach((element) => {
        if (element instanceof HTMLElement) {
          // Skip elements inside lexical editor content
          const isInLexicalContent = element.closest('.static-page-content, .lexical-tailwind-styles, .lexical-editor-content');
          if (isInLexicalContent) {
            return; // Don't apply theme color to lexical editor content
          }
          
          // Skip elements that should be excluded from theme color application
          if (shouldExcludeFromThemeColor(element)) {
            return;
          }
          
          // Skip elements that already have theme-applied class or are inside theme-applied elements
          const parent = element.closest('.theme-applied');
          if (!parent && !element.classList.contains('theme-applied')) {
            // Skip elements that already have inline color style (e.g., from lexical editor)
            const hasInlineColor = element.style.color && element.style.color !== '';
            
            // Only apply text color if the element doesn't have specific color classes or inline color
            const hasSpecificColor = element.className.includes('text-') && 
              !element.className.includes('text-gray-') && 
              !element.className.includes('text-slate-') &&
              !element.className.includes('text-black') &&
              !element.className.includes('text-white');
            
            if (!hasSpecificColor && !hasInlineColor) {
              element.style.color = themeSettings.text_color;
            }
          }
        }
      });
      
      // Apply text color to specific text classes that should use theme color
      const themeTextElements = document.querySelectorAll(`
        .text-gray-900, .text-gray-800, .text-gray-700, .text-gray-600,
        .text-slate-900, .text-slate-800, .text-slate-700, .text-slate-600
      `);
      themeTextElements.forEach((element) => {
        if (element instanceof HTMLElement) {
          if (shouldExcludeFromThemeColor(element)) {
            return;
          }
          // Skip elements that already have inline color style (e.g., from lexical editor)
          const hasInlineColor = element.style.color && element.style.color !== '';
          if (!hasInlineColor) {
            element.style.color = themeSettings.text_color;
          }
        }
      });
      
      // Force white text on primary background elements
      const primaryBackgroundElements = document.querySelectorAll(`
        .bg-primary, 
        [class*="bg-primary"],
        [data-footer-variant="dark-glass"],
        .bg-primary\\/70,
        .bg-primary\\/20,
        .bg-primary\\/10,
        [class*="from-primary"],
        [class*="to-primary"],
        [class*="bg-gradient-to-r from-primary"],
        [class*="bg-gradient-to-l from-primary"],
        [class*="bg-gradient-to-t from-primary"],
        [class*="bg-gradient-to-b from-primary"],
        [class*="bg-gradient-to-r to-primary"],
        [class*="bg-gradient-to-l to-primary"],
        [class*="bg-gradient-to-t to-primary"],
        [class*="bg-gradient-to-b to-primary"]
      `);
      primaryBackgroundElements.forEach((element) => {
        if (element instanceof HTMLElement) {
          // Force white text on primary background
          element.style.color = '#FFFFFF';
          
          // Also apply to all text elements inside
          const textElementsInside = element.querySelectorAll(`
            h1, h2, h3, h4, h5, h6,
            p, span, div, li, td, th,
            label, input, textarea, select,
            .text-primary-foreground,
            .text-white,
            .text-black
          `);
          textElementsInside.forEach((textElement) => {
            if (textElement instanceof HTMLElement) {
              textElement.style.color = '#FFFFFF';
            }
          });
        }
      });
      
      // Use requestAnimationFrame instead of forced reflow for better performance
      // This schedules the repaint without forcing synchronous layout recalculation
      requestAnimationFrame(() => {
        // Repaint will happen naturally in the next frame
      });
      
      // Apply theme again after a longer delay to catch any late-loaded elements
      setTimeout(() => {
        // Check if we're on a dashboard page
        const isDashboardPage = window.location.pathname.includes('/dashboard');
        
        // Don't apply theme colors on dashboard pages
        if (isDashboardPage) {
          return;
        }
        
        // Re-apply text color to any new elements
        const allTextElements = document.querySelectorAll(`
          h1, h2, h3, h4, h5, h6,
          p, span, div, li, td, th,
          .text-gray-900, .text-gray-800, .text-gray-700, .text-gray-600,
          .text-slate-900, .text-slate-800, .text-slate-700, .text-slate-600
        `);
        
        allTextElements.forEach((element) => {
          if (element instanceof HTMLElement) {
            // Skip elements that should be excluded from theme color application
            if (shouldExcludeFromThemeColor(element)) {
              return;
            }
            
            // Check if parent has primary background
            const parent = element.closest('.bg-primary, [class*="bg-primary"], [data-footer-variant="dark-glass"], [class*="from-primary"], [class*="to-primary"]');
            if (parent) {
              // Force white text on primary background
              element.style.color = '#FFFFFF';
            } else {
              // Skip elements that already have specific colors
              const hasSpecificColor = element.className.includes('text-') && 
                !element.className.includes('text-gray-') && 
                !element.className.includes('text-slate-');
              
              if (!hasSpecificColor) {
                element.style.color = themeSettings.text_color;
              }
            }
          }
        });
      }, 500);
    }, 100);
  };

  // Apply initial static theme on mount to prevent any delay or FOUC
  useEffect(() => {
    applyTheme(initialThemeSettings);
  }, []);

  useEffect(() => {
    if (settings) {
      const themeSettings: ThemeSettings = {
        theme_name: settings.theme_name || 'Crystal Blue',
        primary_color: settings.primary_color || '#2563EB',
        secondary_color: settings.secondary_color || '#60A5FA',
        background_color: settings.background_color || '#F9FAFB',
        text_color: settings.text_color || '#1E3A8A',
        brand_color: settings.brand_color || settings.primary_color || '#2563EB',
        dark_mode_enabled: false // Always false for light themes
      };
      applyTheme(themeSettings);
    }
  }, [settings]);

  // Cleanup observer on unmount
  useEffect(() => {
    return () => {
      if (currentObserver) {
        currentObserver.disconnect();
      }
    };
  }, [currentObserver]);

  const value = {
    theme,
    isLoading,
    applyTheme,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
} 
